import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Loader2, BookOpen } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";

const fmt = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const Research = () => {
  const [posts, setPosts] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api
      .get("/blogs")
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]));
  }, []);

  return (
    <div className="min-h-screen bg-cream dark:bg-background text-foreground">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 py-12 md:py-16">
        <Reveal>
          <Eyebrow>Research diary</Eyebrow>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter">
            My written <span className="font-serif italic font-medium marker-highlight">research</span>
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            The story behind the numbers — what I'm trying to find out, how I'm doing it, and
            what the answers are telling me. Written by me, updated as the project grows.
          </p>
        </Reveal>

        {posts === null ? (
          <div className="mt-12 grid place-items-center py-16 text-muted-foreground" data-testid="research-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="mt-10 rounded-3xl border-2 border-dashed border-foreground/15 p-10 text-center" data-testid="research-empty">
            <BookOpen className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="mt-3 font-display font-bold text-lg">No posts yet</p>
            <p className="text-sm text-muted-foreground mt-1">The first write-up is coming soon.</p>
          </div>
        ) : (
          <div className="mt-10 space-y-5">
            {posts.map((b, idx) => {
              const open = openId === b.id;
              return (
                <Reveal key={b.id} delay={0.04 * Math.min(idx, 4)}>
                  <article
                    data-testid="blog-card"
                    className="rounded-3xl bg-card border border-foreground/10 p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <span className="rounded-full bg-flame-soft dark:bg-flame/15 text-flame px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] font-semibold">
                        {b.tag}
                      </span>
                      <span className="font-mono text-[11px] text-muted-foreground">{fmt(b.created_at)}</span>
                    </div>
                    <h2 className="mt-3 font-display text-2xl font-bold tracking-tight leading-snug">{b.title}</h2>
                    <AnimatePresence initial={false}>
                      {open ? (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          {b.body.split("\n").filter(Boolean).map((para, pi) => (
                            <p key={pi} className="mt-3 text-muted-foreground leading-relaxed">{para}</p>
                          ))}
                        </motion.div>
                      ) : (
                        <p className="mt-3 text-muted-foreground leading-relaxed line-clamp-2">{b.body}</p>
                      )}
                    </AnimatePresence>
                    <button
                      data-testid="blog-read-more"
                      onClick={() => setOpenId(open ? null : b.id)}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-pitch dark:text-turf hover:gap-2.5 transition-all"
                    >
                      {open ? "Show less" : "Read the full post"}{" "}
                      <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}

        <div className="mt-16 text-center">
          <Link
            to="/results"
            data-testid="research-to-results"
            className="inline-flex items-center gap-2 h-12 px-7 rounded-full border-2 border-pitch/25 dark:border-turf/40 font-semibold text-pitch dark:text-turf hover:border-pitch dark:hover:border-turf transition-all"
          >
            See the numbers behind the writing
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Research;
